import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApiService, Empregador, Usuario } from '../../api.service';
import { PORTA_PUBLICA_PADRAO_ID_FACE } from './regras-url-api-leitor';

@Injectable({ providedIn: 'root' })
export class ServicoEmpregadorAdministrativo {
  constructor(private readonly api: ApiService) {}

  novoEmpregador(): Empregador {
    return {
      razaoSocial: '',
      nomeFantasia: '',
      documento: '',
      endereco: '',
      cidade: '',
      uf: '',
      slugTenant: '',
      dominio: '',
      modoApiLeitor: 'EXTERNO',
      urlApiLocalLeitor: 'http://192.168.0.10:8080',
      ipApiLeitor: '',
      portaApiLeitor: PORTA_PUBLICA_PADRAO_ID_FACE,
      horarioPrimeiraBatida: '08:00',
      horarioSegundaBatida: '11:00',
      horarioTerceiraBatida: '13:00',
      horarioQuartaBatida: '17:00',
      toleranciaMinutos: 15,
      validarJanelaPonto: true,
      ativo: true
    };
  }

  prepararCadastro(empregador?: Empregador): Empregador {
    const cadastro = empregador ? this.atualizarDominio({ ...empregador }) : this.novoEmpregador();
    return {
      ...cadastro,
      modoApiLeitor: cadastro.modoApiLeitor || 'EXTERNO',
      urlApiLocalLeitor: cadastro.urlApiLocalLeitor || 'http://192.168.0.10:8080',
      ipApiLeitor: cadastro.ipApiLeitor || '',
      portaApiLeitor: cadastro.portaApiLeitor ?? PORTA_PUBLICA_PADRAO_ID_FACE
    };
  }

  atualizarDominio(empregador: Empregador): Empregador {
    const tenant = this.normalizarTenant(empregador.slugTenant || '');
    return {
      ...empregador,
      slugTenant: tenant,
      dominio: tenant ? `${tenant}.jwmtecnologia.com.br` : ''
    };
  }

  comTenant(empregador: Empregador, slugTenant: string): Empregador {
    return this.atualizarDominio({
      ...empregador,
      slugTenant
    });
  }

  comLogo(empregador: Empregador, logo: Pick<Empregador, 'logoBase64' | 'logoMimeType'>): Empregador {
    return {
      ...empregador,
      logoBase64: logo.logoBase64,
      logoMimeType: logo.logoMimeType
    };
  }

  semLogo(empregador: Empregador): Empregador {
    return {
      ...empregador,
      logoBase64: '',
      logoMimeType: ''
    };
  }

  normalizarTenant(valor: string) {
    return valor
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  async carregarLogoArquivo(arquivo: File): Promise<Pick<Empregador, 'logoBase64' | 'logoMimeType'>> {
    if (!arquivo.type.startsWith('image/')) {
      throw new Error('Selecione um arquivo de imagem para a logo.');
    }

    const logoBase64 = await this.lerArquivoComoDataUrl(arquivo);
    return {
      logoBase64,
      logoMimeType: arquivo.type
    };
  }

  listar(): Observable<Empregador[]> {
    return this.api.listarEmpregadores();
  }

  listarDaSessao(usuario?: Usuario): Observable<Empregador[]> {
    if (usuario?.perfil === 'ADMIN_EMPREGADOR' && usuario.empregador) {
      return of([usuario.empregador]);
    }

    return this.listar();
  }

  salvar(empregador: Empregador): Observable<Empregador> {
    return this.api.salvarEmpregador(this.atualizarDominio(empregador));
  }

  excluir(empregadorId: string): Observable<void> {
    return this.api.excluirEmpregador(empregadorId);
  }

  gerarAcessos(empregadorId?: string) {
    return this.api.gerarAcessosServidor(empregadorId);
  }

  mensagemEmpregadorSalvo(_empregador: Empregador, editando = false) {
    return editando
      ? 'Sucesso.\nRegistro alterado com sucesso.'
      : 'Sucesso.\nRegistro salvo com sucesso.';
  }

  mensagemErroCarregarLogo() {
    return 'Erro ao carregar logo do empregador.';
  }

  mensagemErroSalvar() {
    return 'Erro ao salvar empregador.';
  }

  mensagemConfirmarExclusao() {
    return 'Confirma excluir este empregador?';
  }

  mensagemEmpregadorExcluido() {
    return 'Sucesso.\nRegistro excluído com sucesso.';
  }

  mensagemErroExcluir() {
    return 'Erro ao excluir empregador.';
  }

  private lerArquivoComoDataUrl(arquivo: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(arquivo);
    });
  }
}
